import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Switch } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import NetworkConcernsContext from '@totem/components/networkConcerns/networkConcernsContext';
import { NetworkInterfaceConcern } from '@totem/components/networkConcerns/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import colors from '@totem/styles/colors';
import { isNotNull } from '@totem/utilities/common';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { sortStringAscending } from '@totem/utilities/tableUtilities';
type Props = {
  data: NetworkInterfaceConcern[];
};
const DeviceInterfaceTable = ({ data }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { loading, onAction } = useContext(NetworkConcernsContext);
  const [isSending, setIsSending] = useState<boolean>(false);
  const canApproveInterfaces = useMemo(() => {
    return permissions.internalOrganizationalAdministrator.hasPermissions(
      userProfile,
    );
  }, [userProfile]);
  const sendApprovalUpdate = useCallback(
    async (deviceId: string, macAddress: string, approval: boolean) => {
      if (isSending) {
        return;
      }
      setIsSending(true);
      const endPointAction = approval ? 'approve' : 'revoke';
      fetch(
        `${DEVICES_ENDPOINT}/${deviceId}/networkInterface/${macAddress}/${endPointAction}`,
        {
          method: 'PUT',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
        },
      ).then((res) => {
        CheckResponseShowError(res);

        setIsSending(false);

        onAction('refresh');
      });
    },
    [isSending],
  );
  const updateApproval = (
    deviceId: string,
    macAddress: string,
    approval: boolean,
  ) => {
    const action = approval
      ? 'approve the interface'
      : 'revoke approval for the interface';
    const okText = approval ? 'Approve' : 'Revoke';
    Modal.confirm({
      title: 'Are you sure?',
      content: `Please confirm that you would like to ${action}.`,
      okText,
      okType: 'danger',
      cancelText: 'Cancel',
      open: false,
      onOk: () => sendApprovalUpdate(deviceId, macAddress, approval),
    });
  };
  const columns: ColumnProps<NetworkInterfaceConcern>[] = [
    {
      title: 'Name',
      dataIndex: 'device.displayName',
      key: 'displayName',
      showSorterTooltip: true,
      render: (_, record: NetworkInterfaceConcern) => (
        <Link to={`/dashboard/devices/${record.device.device.id}`}>
          {record.device.device.displayName}
        </Link>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.device.device.displayName,
          compB.device.device.displayName,
        ),
    },
    {
      title: 'Building',
      dataIndex: 'device.building.name',
      key: 'device.building.name',
      showSorterTooltip: true,
      render: (_, record: NetworkInterfaceConcern) =>
        record.device.building.name,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.device.building.name,
          compB.device.building.name,
        ),
    },
    {
      title: 'Interface Name',
      dataIndex: 'interfaces.interfaceName',
      key: 'interfaces.interfaceName',
      showSorterTooltip: true,
      render: (_, record: NetworkInterfaceConcern) => (
        <>
          {record.interfaces.map((netInf) => (
            <div key={netInf.macAddress}>{netInf.details.interfaceName}</div>
          ))}
        </>
      ),
    },
    {
      title: 'MAC Addresses',
      dataIndex: 'interfaces.macAddress',
      key: 'interfaces.macAddress',
      showSorterTooltip: true,
      render: (_, record: NetworkInterfaceConcern) => (
        <>
          {record.interfaces.map((netInf) => (
            <div key={netInf.macAddress}>
              {netInf.details.macAddress.toString()}
            </div>
          ))}
        </>
      ),
    },
    {
      title: 'IP Addresses',
      dataIndex: 'interfaces.ipAddress',
      key: 'interfaces.ipAddress',
      showSorterTooltip: true,
      render: (_, record: NetworkInterfaceConcern) => (
        <>
          {record.interfaces.map((netInf) => (
            <div key={netInf.macAddress}>
              {netInf.details.ipAddress.toString()}
            </div>
          ))}
        </>
      ),
    },
    {
      title: 'Default Gateway',
      dataIndex: 'interfaces.defaultGateway',
      key: 'interfaces.defaultGateway',
      showSorterTooltip: true,
      render: (_, record: NetworkInterfaceConcern) => (
        <>
          {record.interfaces.map((netInf) => (
            <div key={netInf.macAddress}>
              {netInf.details.defaultGateway.toString()}
            </div>
          ))}
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'interfaces.status',
      key: 'interfaces.status',
      showSorterTooltip: true,
      render: (_, record: NetworkInterfaceConcern) => (
        <>
          {record.interfaces.map((netInf) => (
            <div
              key={netInf.macAddress}
              style={{
                color:
                  netInf.details.status === 'Up'
                    ? colors.event.criticality.background.ok
                    : colors.event.criticality.background.critical,
              }}
            >
              {netInf.details.status}
            </div>
          ))}
        </>
      ),
    },
    {
      title: 'Approved',
      dataIndex: 'interfaces.approved',
      key: 'interfaces.approved',
      showSorterTooltip: true,
      render: (_, record: NetworkInterfaceConcern) => (
        <>
          {record.interfaces.map((netInf) => (
            <div key={netInf.macAddress}>
              <Switch
                value={netInf.approved}
                disabled={!canApproveInterfaces}
                style={{
                  backgroundColor: netInf.approved
                    ? colors.event.criticality.background.ok
                    : colors.event.criticality.background.critical,
                }}
                onChange={(newValue) =>
                  updateApproval(
                    record.device.device.id,
                    netInf.macAddress,
                    newValue,
                  )
                }
              />
            </div>
          ))}
        </>
      ),
    },
  ];
  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={isNotNull(data) ? data : []}
      loading={loading}
      pagination={{
        showSizeChanger: true,
      }}
      rowKey={(record) => record.device.device.id}
    />
  );
};
export default DeviceInterfaceTable;
